import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Row, Space } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { TeamMemberResponse } from 'store/teams/types';
import TeamMembersTable from '../../components/MembersTable';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import { IndividualType } from 'store/individuals/individual';
import { individualTypeText } from 'views/user-management/utils';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useTeams from 'hooks/useTeams';
import Notification from 'components/ui/base/notification/Notification';
import { AxiosError } from 'axios';
import useSession from 'hooks/useSession';

type FilterValues = { q: string; role?: IndividualType | null };

const normalize = (s?: string) => (s ?? '').toLowerCase().trim();

const matchesQuery = (m: TeamMemberResponse, q: string) => {
    if (!q) return true;
    const n = normalize(q);
    return normalize(m.individual.fullName).includes(n) || normalize(m.individual.email).includes(n);
};

const matchesRole = (m: TeamMemberResponse, role?: IndividualType | null) => !role || m.role === role;

const teamRolesOptions: SelectOption[] = [
    {
        id: IndividualType.TEAM_MANAGER,
        value: individualTypeText[IndividualType.TEAM_MANAGER],
    },
    {
        id: IndividualType.JOB_MANAGER,
        value: individualTypeText[IndividualType.JOB_MANAGER],
    },
    {
        id: IndividualType.CLEARANCE_AGENT,
        value: individualTypeText[IndividualType.CLEARANCE_AGENT],
    },
];

const MembersTab = () => {
    const { userInfo } = useSession();
    const { teamId } = useParams();
    const { team, getTeamById } = useTeams(teamId);
    const [filters, setFilters] = useState<FilterValues>({ q: '', role: null });
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        // Client-side pagination
        current: 1,
        pageSize: 10,
        position: ['bottomCenter'],
    });

    const filtered = useMemo(() => {
        const members = team?.members ?? [];
        return members.filter((m) => matchesQuery(m, filters.q) && matchesRole(m, filters.role));
    }, [team?.members, filters.q, filters.role]);

    // Keep the pagination in sycn when we filter
    useEffect(() => {
        setPagination((p) => ({ ...p, total: filtered.length, current: 1 }));
    }, [filtered.length]);

    // Get initial team data
    useEffect(() => {
        if (teamId) {
            getTeamById(teamId);
        }
    }, [getTeamById, teamId]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row>
                <Space size={8}>
                    <SearchBar
                        allowClear
                        inputPlaceholder="Search team members"
                        onChange={(value: string) => setFilters((f) => ({ ...f, q: value }))}
                    />
                    <FormSelect
                        hideKeys
                        style={{ width: 200 }}
                        placeholder="By Role"
                        selectOptions={teamRolesOptions}
                        onClear={() => setFilters((f) => ({ ...f, role: undefined }))}
                        onChange={(role: IndividualType | null) => setFilters((f) => ({ ...f, role }))}
                    />
                </Space>
            </Row>

            <TeamMembersTable
                userInfo={userInfo}
                teams={filtered}
                pagination={pagination}
                // Note: For now, we don't need to have delete functionality here
                // onRemoveTeamMember={handleRemoveIndividualFromTeam}
                onChange={(antPagination) => {
                    setPagination((p) => ({
                        ...p,
                        current: antPagination.current,
                    }));
                }}
                onEditTeamMember={() => {
                    if (!teamId) {
                        return;
                    }
                    getTeamById(teamId);
                }}
            />
        </Space>
    );
};

export default MembersTab;
